import React, { useState, useEffect } from "react";
import {
  Activity,
  Cpu,
  HardDrive,
  Tv,
  Film,
  FolderSync,
  Settings,
  Trash2,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  Users,
  UserPlus,
  Key,
  Shield,
  UserCheck,
  UserX,
  Zap,
  Check,
  X,
  Lock,
  Bell,
  Laptop,
  Send,
  FileText,
  Terminal,
} from "lucide-react";
import { api } from "../lib/api";
import { useThemeLanguage } from "../context/ThemeLanguageContext";

export function AdminPage() {
  const { language, isRtl } = useThemeLanguage();
  const isHebrew = language === "he";

  const [activeTab, setActiveTab] = useState<"overview" | "users" | "streams" | "library" | "settings" | "webhooks" | "sessions" | "logs">("overview");
  const [health, setHealth] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [streams, setStreams] = useState<any[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [usersList, setUsersList] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState<"all" | "admin" | "user">("all");
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Playback & Diagnostic Logs
  const [playbackLogs, setPlaybackLogs] = useState<any[]>([]);
  const [logsSummary, setLogsSummary] = useState<any>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsCategoryFilter, setLogsCategoryFilter] = useState("ALL");
  const [logsLevelFilter, setLogsLevelFilter] = useState("ALL");
  const [logsSearch, setLogsSearch] = useState("");
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Webhooks & Sessions state
  const [webhookSettings, setWebhookSettings] = useState<any>({
    discordWebhookUrl: "",
    telegramBotToken: "",
    telegramChatId: "",
    notifyOnScan: true,
    notifyOnDownload: true,
    notifyOnError: true,
  });
  const [testingWebhook, setTestingWebhook] = useState<"discord" | "telegram" | null>(null);
  const [sessionsList, setSessionsList] = useState<any[]>([]);

  // User modals state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<"admin" | "user">("user");

  const [resetPasswordUser, setResetPasswordUser] = useState<any | null>(null);
  const [resetPasswordInput, setResetPasswordInput] = useState("");

  // Fix match modal state
  const [fixMatchItem, setFixMatchItem] = useState<any | null>(null);
  const [tmdbIdInput, setTmdbIdInput] = useState("");
  const [aliasInput, setAliasInput] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const [h, s, str, set, u, wh, ses] = await Promise.all([
        api.getAdminHealth().catch(() => null),
        api.getAdminSummary().catch(() => null),
        api.getAdminStreams().catch(() => []),
        api.getAdminSettings().catch(() => ({})),
        api.getAdminUsers().catch(() => []),
        api.getAdminWebhooks().catch(() => ({})),
        api.getAdminSessions().catch(() => []),
      ]);
      setHealth(h);
      setSummary(s);
      setStreams(str);
      setSettings(set);
      setUsersList(u);
      if (wh) setWebhookSettings((prev: any) => ({ ...prev, ...wh }));
      if (ses) setSessionsList(ses);
    } catch (err) {
      console.error("Failed to load admin data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === "logs") {
      setLogsLoading(true);
      Promise.all([
        api.getPlaybackLogs({
          category: logsCategoryFilter === "ALL" ? undefined : logsCategoryFilter,
          level: logsLevelFilter === "ALL" ? undefined : logsLevelFilter,
          search: logsSearch || undefined,
          limit: 150,
        }).catch(() => []),
        api.getPlaybackSummary().catch(() => null),
      ]).then(([logs, sum]) => {
        setPlaybackLogs(logs || []);
        if (sum) setLogsSummary(sum);
      }).finally(() => {
        setLogsLoading(false);
      });
    }
  }, [activeTab, logsCategoryFilter, logsLevelFilter, logsSearch]);

  const handleSaveWebhooks = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.updateAdminWebhooks(webhookSettings);
      setActionMessage(isHebrew ? "הגדרות ההתראות נשמרו בהצלחה!" : "Webhook settings saved successfully!");
      setTimeout(() => setActionMessage(null), 4000);
    } catch (err: any) {
      alert(`Error saving webhooks: ${err.message}`);
    }
  };

  const handleTestWebhook = async (type: "discord" | "telegram") => {
    setTestingWebhook(type);
    try {
      const res = await api.testAdminWebhook(type);
      setActionMessage(res.message || (isHebrew ? "התראת בדיקה נשלחה בהצלחה!" : "Test notification sent!"));
      setTimeout(() => setActionMessage(null), 4000);
    } catch (err: any) {
      alert(`Test webhook error: ${err.message}`);
    } finally {
      setTestingWebhook(null);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!confirm(isHebrew ? "האם לנתק סשן זה?" : "Revoke this session?")) return;
    try {
      await api.revokeAdminSession(sessionId);
      setSessionsList((prev) => prev.filter((s) => s.id !== sessionId));
      setActionMessage(isHebrew ? "הסשן נותק בהצלחה" : "Session revoked successfully");
      setTimeout(() => setActionMessage(null), 3000);
    } catch (err: any) {
      alert(`Error revoking session: ${err.message}`);
    }
  };

  const handleTriggerScan = async () => {
    try {
      await api.triggerScan();
      setActionMessage(isHebrew ? "סריקת הספרייה הופעלה בהצלחה!" : "Library scan triggered!");
      setTimeout(() => setActionMessage(null), 4000);
      loadData();
    } catch (err: any) {
      alert(`Error triggering scan: ${err.message}`);
    }
  };

  const handleClearCache = async () => {
    if (!confirm(isHebrew ? "האם אתה בטוח שברצונך לנקות את המטמון והטרנסקודינג?" : "Are you sure you want to clear the transcoding cache and metadata cache?")) return;
    try {
      await api.clearCache();
      setActionMessage(isHebrew ? "המטמון נוקה בהצלחה!" : "Cache cleared successfully!");
      setTimeout(() => setActionMessage(null), 4000);
    } catch (err: any) {
      alert(`Error clearing cache: ${err.message}`);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.updateAdminSettings(settings);
      setActionMessage(isHebrew ? "ההגדרות נשמרו בהצלחה!" : "Settings saved successfully!");
      setTimeout(() => setActionMessage(null), 4000);
    } catch (err: any) {
      alert(`Error saving settings: ${err.message}`);
    }
  };

  const handleFixMatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fixMatchItem || !tmdbIdInput) return;

    try {
      await api.fixMatch({
        mediaItemId: fixMatchItem.id,
        tmdbId: parseInt(tmdbIdInput, 10),
        type: fixMatchItem.type || "movie",
        customAlias: aliasInput || undefined,
      });
      setFixMatchItem(null);
      setTmdbIdInput("");
      setAliasInput("");
      setActionMessage(isHebrew ? "הקישור עודכן בהצלחה!" : "Match updated successfully!");
      setTimeout(() => setActionMessage(null), 4000);
      loadData();
    } catch (err: any) {
      alert(`Error fixing match: ${err.message}`);
    }
  };

  // --- User Management Handlers ---
  const handleToggleUser = async (userId: string) => {
    try {
      await api.toggleAdminUserActive(userId);
      setActionMessage(isHebrew ? "סטטוס המשתמש עודכן" : "User status toggled");
      setTimeout(() => setActionMessage(null), 3000);
      const updatedUsers = await api.getAdminUsers();
      setUsersList(updatedUsers);
    } catch (err: any) {
      alert(err.message || "Failed to toggle user status");
    }
  };

  const handleToggleRole = async (user: any) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    try {
      await api.updateAdminUserRole(user.id, newRole);
      setActionMessage(isHebrew ? `תפקיד המשתמש שונה ל-${newRole}` : `User role updated to ${newRole}`);
      setTimeout(() => setActionMessage(null), 3000);
      const updatedUsers = await api.getAdminUsers();
      setUsersList(updatedUsers);
    } catch (err: any) {
      alert(err.message || "Failed to update user role");
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createAdminUser({
        username: newUsername,
        displayName: newDisplayName || newUsername,
        password: newPassword,
        role: newRole,
      });
      setShowCreateUserModal(false);
      setNewUsername("");
      setNewDisplayName("");
      setNewPassword("");
      setActionMessage(isHebrew ? `משתמש חדש נוצר בהצלחה!` : `User created successfully!`);
      setTimeout(() => setActionMessage(null), 3000);
      const updatedUsers = await api.getAdminUsers();
      setUsersList(updatedUsers);
    } catch (err: any) {
      alert(err.message || "Failed to create user");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUser || !resetPasswordInput) return;
    try {
      await api.resetAdminUserPassword(resetPasswordUser.id, resetPasswordInput);
      setResetPasswordUser(null);
      setResetPasswordInput("");
      setActionMessage(isHebrew ? `סיסמת המשתמש אופסה בהצלחה!` : `Password reset successfully!`);
      setTimeout(() => setActionMessage(null), 3000);
    } catch (err: any) {
      alert(err.message || "Failed to reset password");
    }
  };

  const handleDeleteUser = async (user: any) => {
    if (!confirm(isHebrew ? `האם אתה בטוח שברצונך למחוק את המשתמש ${user.username}?` : `Are you sure you want to delete user ${user.username}?`)) return;
    try {
      await api.deleteAdminUser(user.id);
      setActionMessage(isHebrew ? `המשתמש נמחק בהצלחה` : `User deleted successfully`);
      setTimeout(() => setActionMessage(null), 3000);
      const updatedUsers = await api.getAdminUsers();
      setUsersList(updatedUsers);
    } catch (err: any) {
      alert(err.message || "Failed to delete user");
    }
  };

  const filteredUsers = usersList.filter((u) => {
    const matchesSearch =
      u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.displayName && u.displayName.toLowerCase().includes(userSearch.toLowerCase()));
    if (userFilter === "admin") return matchesSearch && u.role === "admin";
    if (userFilter === "user") return matchesSearch && u.role === "user";
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white p-4 md:p-10 pt-28 md:pt-32 max-w-7xl mx-auto admin-page" dir={isRtl ? "rtl" : "ltr"}>
      {/* Admin Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-white/10">
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-black tracking-tight text-white">
              {isHebrew ? "לוח בקרת שרת Homeflix" : "Homeflix Server Control"}
            </h1>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-900 border border-white/10 text-xs shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="font-semibold text-neutral-300">
                {isHebrew ? "ניהול מנהל מערכת" : "System Administrator"}
              </span>
              <span className="text-neutral-500">•</span>
              <code className="text-emerald-400 font-mono font-medium">100.127.161.16:8096</code>
            </div>
          </div>
          <p className="text-xs text-neutral-400">
            {isHebrew
              ? "מרכז שליטה ראשי: ניהול משתמשים והרשאות, הגדרות מערכת, קידוד וידאו בזמן אמת וסריקת סרטים."
              : "Dedicated host control plane. Real-time telemetry, user management, and media server settings."}
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {isHebrew ? "רענן נתונים" : "Refresh"}
          </button>
          <button
            onClick={handleTriggerScan}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#E50914] hover:bg-[#b80710] text-sm font-bold shadow-lg shadow-[#E50914]/30 transition-colors cursor-pointer"
          >
            <FolderSync className="w-4 h-4" />
            {isHebrew ? "סרוק ספרייה" : "Scan Library"}
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className="my-4 p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-sm font-medium flex items-center gap-2 animate-in fade-in">
          <CheckCircle className="w-4 h-4" />
          {actionMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-white/10 mt-6 gap-2 sm:gap-6 overflow-x-auto pb-1">
        {[
          { id: "overview", label: isHebrew ? "מצב מערכת" : "Overview", icon: Activity },
          { id: "users", label: isHebrew ? `ניהול משתמשים (${usersList.length})` : `Users (${usersList.length})`, icon: Users },
          { id: "streams", label: isHebrew ? `שידורים פעילים (${streams.length})` : `Streams (${streams.length})`, icon: Tv },
          { id: "library", label: isHebrew ? "ספרייה ושמות" : "Library & Matches", icon: Film },
          { id: "logs", label: isHebrew ? `יומן ניגון ושגיאות` : `Playback Logs`, icon: FileText },
          { id: "webhooks", label: isHebrew ? "התראות Webhooks" : "Webhooks", icon: Bell },
          { id: "sessions", label: isHebrew ? `סשנים פעילים (${sessionsList.length})` : `Sessions (${sessionsList.length})`, icon: Laptop },
          { id: "settings", label: isHebrew ? "הגדרות שרת" : "Settings", icon: Settings },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 pb-4 text-sm font-bold border-b-2 transition-colors shrink-0 cursor-pointer ${
                activeTab === tab.id
                  ? "border-[#E50914] text-white"
                  : "border-transparent text-neutral-400 hover:text-neutral-200"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── TAB 1: System Overview ─── */}
      {activeTab === "overview" && (
        <div className="py-8 space-y-8 animate-in fade-in">
          {/* Quick Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs text-neutral-400 font-medium">{isHebrew ? "משתמשים רשומים" : "Total Users"}</p>
                <h3 className="text-2xl font-black text-white">{usersList.length}</h3>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-red-500/10 text-[#E50914] border border-red-500/20">
                <Film className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs text-neutral-400 font-medium">{isHebrew ? "סרטים וסדרות" : "Library Media"}</p>
                <h3 className="text-2xl font-black text-white">{(summary?.movies || 0) + (summary?.shows || 0)}</h3>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
                <Tv className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs text-neutral-400 font-medium">{isHebrew ? "שידורים ישירים" : "Active Streams"}</p>
                <h3 className="text-2xl font-black text-white">{streams.length}</h3>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <Cpu className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs text-neutral-400 font-medium">{isHebrew ? "מאיץ גרפי (GPU)" : "GPU Transcoder"}</p>
                <h3 className="text-base font-bold text-white truncate max-w-[150px]">
                  {health?.system?.gpu?.model || "Auto (NVENC)"}
                </h3>
              </div>
            </div>
          </div>

          {/* Telemetry Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-neutral-900/60 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Cpu className="w-5 h-5 text-neutral-400" />
                {isHebrew ? "נתוני מעבד וזיכרון" : "Host CPU & Memory"}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "דגם מעבד:" : "Processor:"}</span>
                  <span className="font-semibold text-white">{health?.system?.cpu?.model || "Unknown"}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "ליבות מעבד:" : "CPU Cores:"}</span>
                  <span className="font-mono text-white">{health?.system?.cpu?.cores || health?.system?.cpuCount || 1} Cores</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "עומס (Load Avg):" : "Load Average:"}</span>
                  <span className="font-mono text-emerald-400">
                    {health?.system?.loadAvg?.join(", ") || health?.system?.cpu?.loadAvg?.map((n: number) => n.toFixed(2)).join(", ") || "0.00"}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "זיכרון RAM פנוי:" : "RAM Available:"}</span>
                  <span className="font-mono text-white">
                    {health?.system?.freeMemoryMb
                      ? `${(health.system.freeMemoryMb / 1024).toFixed(1)} GB / ${(health.system.totalMemoryMb / 1024).toFixed(1)} GB`
                      : health?.system?.memory
                      ? `${Math.round(health.system.memory.freeBytes / (1024 * 1024 * 1024))} GB / ${Math.round(health.system.memory.totalBytes / (1024 * 1024 * 1024))} GB`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-neutral-400">{isHebrew ? "בסיס נתונים & Redis:" : "DB & Redis Status:"}</span>
                  <span className="font-mono text-emerald-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>{health?.database?.status || "healthy"} ({health?.database?.latencyMs ?? 1}ms)</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-neutral-900/60 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-neutral-400" />
                {isHebrew ? "סטטוס שרת ומדיה" : "Server & Media Status"}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "זמן פעילות (Uptime):" : "Uptime:"}</span>
                  <span className="font-mono text-white">
                    {health?.uptime ? `${Math.floor(health.uptime / 3600)}h ${Math.floor((health.uptime % 3600) / 60)}m` : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "גרסת שרת:" : "App Version:"}</span>
                  <span className="font-mono text-white">{health?.version || "0.1.0"}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-neutral-400">{isHebrew ? "קובצי וידאו בספרייה:" : "Total Media Files:"}</span>
                  <span className="font-mono text-white">{summary?.mediaFiles || 0}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-neutral-400">{isHebrew ? "סורק רקע:" : "Scanner Watcher:"}</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-400">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    {summary?.scannerStatus?.isScanning ? (isHebrew ? "סורק כעת..." : "Scanning now...") : (isHebrew ? "פעיל ומאזין" : "Active & Watching")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 2: Users & Permissions Management ─── */}
      {activeTab === "users" && (
        <div className="py-8 space-y-6 animate-in fade-in">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className={`absolute ${isRtl ? "right-3.5" : "left-3.5"} top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400`} />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder={isHebrew ? "חיפוש משתמש לפי שם..." : "Search user by name..."}
                  className={`w-full ${isRtl ? "pr-10 pl-4" : "pl-10 pr-4"} py-2 bg-neutral-900 border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#E50914]`}
                />
              </div>
              <div className="flex items-center gap-1 bg-neutral-900 border border-white/10 rounded-xl p-1">
                <button
                  onClick={() => setUserFilter("all")}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    userFilter === "all" ? "bg-white/20 text-white" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {isHebrew ? "הכל" : "All"}
                </button>
                <button
                  onClick={() => setUserFilter("admin")}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    userFilter === "admin" ? "bg-[#E50914] text-white" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {isHebrew ? "מנהלים" : "Admins"}
                </button>
                <button
                  onClick={() => setUserFilter("user")}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    userFilter === "user" ? "bg-blue-600 text-white" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {isHebrew ? "משתמשים" : "Users"}
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowCreateUserModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#E50914] to-[#b80710] hover:brightness-110 text-white font-bold text-sm shadow-lg shadow-[#E50914]/20 transition-all cursor-pointer"
            >
              <UserPlus className="w-4 h-4" />
              <span>{isHebrew ? "הוסף משתמש חדש" : "Add New User"}</span>
            </button>
          </div>

          {/* Users Table / Grid */}
          <div className="rounded-2xl bg-neutral-900/60 border border-white/10 overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left" dir={isRtl ? "rtl" : "ltr"}>
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.02] text-xs uppercase font-bold text-neutral-400 tracking-wider">
                    <th className="py-3.5 px-4">{isHebrew ? "משתמש" : "User"}</th>
                    <th className="py-3.5 px-4">{isHebrew ? "תפקיד והרשאה" : "Role"}</th>
                    <th className="py-3.5 px-4">{isHebrew ? "סטטוס חשבון" : "Status"}</th>
                    <th className="py-3.5 px-4">{isHebrew ? "תאריך הרשמה" : "Joined"}</th>
                    <th className="py-3.5 px-4 text-center">{isHebrew ? "פעולות מנהל" : "Actions"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-neutral-500 text-xs">
                        {isHebrew ? "לא נמצאו משתמשים התואמים לחיפוש" : "No users matched your search criteria"}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => {
                      const isPrimaryAdmin = u.username === "admin" || u.username === "idodi";
                      return (
                        <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-neutral-800 to-neutral-700 border border-white/10 flex items-center justify-center font-bold text-xs text-white">
                                {u.username.substring(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-bold text-white flex items-center gap-1.5">
                                  <span>{u.displayName || u.username}</span>
                                  {u.role === "admin" && (
                                    <Shield className="w-3.5 h-3.5 text-[#E50914] fill-current" />
                                  )}
                                </div>
                                <span className="text-xs text-neutral-400 font-mono">@{u.username}</span>
                              </div>
                            </div>
                          </td>

                          <td className="py-4 px-4">
                            <button
                              onClick={() => !isPrimaryAdmin && handleToggleRole(u)}
                              disabled={isPrimaryAdmin}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition-all ${
                                u.role === "admin"
                                  ? "bg-red-500/20 text-[#E50914] border border-red-500/30"
                                  : "bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 cursor-pointer"
                              } ${isPrimaryAdmin ? "cursor-not-allowed opacity-90" : ""}`}
                              title={isPrimaryAdmin ? "Primary Admin" : "Click to toggle role"}
                            >
                              {u.role === "admin" ? (
                                <>
                                  <Shield className="w-3 h-3" />
                                  <span>{isHebrew ? "מנהל מערכת" : "Admin"}</span>
                                </>
                              ) : (
                                <>
                                  <span>{isHebrew ? "משתמש רגיל" : "Member"}</span>
                                </>
                              )}
                            </button>
                          </td>

                          <td className="py-4 px-4">
                            <button
                              onClick={() => !isPrimaryAdmin && handleToggleUser(u.id)}
                              disabled={isPrimaryAdmin}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition-all ${
                                u.isActive
                                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                                  : "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                              } ${isPrimaryAdmin ? "cursor-not-allowed opacity-90" : "cursor-pointer hover:brightness-125"}`}
                            >
                              <span className={`w-2 h-2 rounded-full ${u.isActive ? "bg-emerald-400" : "bg-rose-400"}`} />
                              <span>{u.isActive ? (isHebrew ? "פעיל" : "Active") : (isHebrew ? "מושעה" : "Suspended")}</span>
                            </button>
                          </td>

                          <td className="py-4 px-4 text-xs text-neutral-400 font-mono">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "N/A"}
                          </td>

                          <td className="py-4 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => {
                                  setResetPasswordUser(u);
                                  setResetPasswordInput("");
                                }}
                                className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-300 hover:text-white transition-colors cursor-pointer"
                                title={isHebrew ? "איפוס סיסמה למשתמש זה" : "Reset Password"}
                              >
                                <Key className="w-4 h-4 text-amber-400" />
                              </button>

                              {!isPrimaryAdmin && (
                                <button
                                  onClick={() => handleDeleteUser(u)}
                                  className="p-1.5 rounded-lg bg-white/5 hover:bg-rose-500/20 text-neutral-400 hover:text-rose-400 transition-colors cursor-pointer"
                                  title={isHebrew ? "מחק משתמש זה" : "Delete User"}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal: Create User */}
          {showCreateUserModal && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
              <div className="w-full max-w-md bg-[#161618] border border-white/20 rounded-2xl p-6 shadow-2xl animate-modal-sheet text-white">
                <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-4">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-[#E50914]" />
                    <h3 className="text-lg font-bold">{isHebrew ? "הוספת משתמש חדש" : "Add New User"}</h3>
                  </div>
                  <button onClick={() => setShowCreateUserModal(false)} className="text-neutral-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      {isHebrew ? "שם משתמש (Username)" : "Username"}
                    </label>
                    <input
                      type="text"
                      required
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="e.g. danny"
                      className="w-full px-3 py-2 bg-neutral-900 border border-white/20 rounded-lg text-sm text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      {isHebrew ? "שם תצוגה (Display Name)" : "Display Name"}
                    </label>
                    <input
                      type="text"
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      placeholder="e.g. Danny Cohen"
                      className="w-full px-3 py-2 bg-neutral-900 border border-white/20 rounded-lg text-sm text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      {isHebrew ? "סיסמה (Password)" : "Password"}
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full px-3 py-2 bg-neutral-900 border border-white/20 rounded-lg text-sm text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      {isHebrew ? "תפקיד הרשאה" : "Role"}
                    </label>
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value as any)}
                      className="w-full px-3 py-2 bg-neutral-900 border border-white/20 rounded-lg text-sm text-white"
                    >
                      <option value="user">{isHebrew ? "משתמש רגיל (Member)" : "Regular User"}</option>
                      <option value="admin">{isHebrew ? "מנהל מערכת (Admin)" : "Administrator"}</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => setShowCreateUserModal(false)}
                      className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold"
                    >
                      {isHebrew ? "ביטול" : "Cancel"}
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 rounded-lg bg-[#E50914] hover:bg-[#b80710] text-xs font-bold shadow-lg shadow-[#E50914]/30"
                    >
                      {isHebrew ? "צור משתמש" : "Create User"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: Reset Password */}
          {resetPasswordUser && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
              <div className="w-full max-w-md bg-[#161618] border border-white/20 rounded-2xl p-6 shadow-2xl animate-modal-sheet text-white">
                <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-4">
                  <div className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-bold">
                      {isHebrew ? `איפוס סיסמה עבור @${resetPasswordUser.username}` : `Reset Password for @${resetPasswordUser.username}`}
                    </h3>
                  </div>
                  <button onClick={() => setResetPasswordUser(null)} className="text-neutral-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      {isHebrew ? "סיסמה חדשה (לפחות 6 תווים)" : "New Password (min 6 chars)"}
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      autoFocus
                      value={resetPasswordInput}
                      onChange={(e) => setResetPasswordInput(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full px-3 py-2 bg-neutral-900 border border-white/20 rounded-lg text-sm text-white focus:border-[#E50914]"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => setResetPasswordUser(null)}
                      className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold"
                    >
                      {isHebrew ? "ביטול" : "Cancel"}
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-xs font-bold text-white shadow-lg"
                    >
                      {isHebrew ? "שמור סיסמה חדשה" : "Update Password"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB 3: Active Streams ─── */}
      {activeTab === "streams" && (
        <div className="py-8 space-y-4 animate-in fade-in">
          {streams.length === 0 ? (
            <div className="py-16 text-center text-neutral-500 space-y-2">
              <Tv className="w-12 h-12 mx-auto text-neutral-600" />
              <p className="text-base font-semibold text-white">
                {isHebrew ? "אין שידורים פעילים כרגע" : "No active streams at the moment"}
              </p>
              <p className="text-xs">
                {isHebrew ? "כאשר מכשירים צופים בסרטים בבית או ברשת, השידורים יופיעו כאן בזמן אמת." : "When clients start streaming movies, active sessions will appear here in real time."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {streams.map((s: any) => (
                <div
                  key={s.id}
                  className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-white truncate max-w-[200px]">{s.title || "Unknown File"}</h4>
                    <span
                      className={`px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                        s.type === "direct"
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                          : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                      }`}
                    >
                      {s.type === "direct" ? "Direct Play" : `Transcode (${s.quality || "720p"})`}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-400 space-y-1">
                    <p>Client IP: <span className="font-mono text-neutral-300">{s.clientIp || "Local"}</span></p>
                    <p>Started: <span className="font-mono text-neutral-300">{new Date(s.startedAt).toLocaleTimeString()}</span></p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── TAB 4: Library & Fix Match ─── */}
      {activeTab === "library" && (
        <div className="py-8 space-y-6 animate-in fade-in">
          <div className="p-6 rounded-2xl bg-neutral-900/60 border border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-white mb-1">
                {isHebrew ? "סריקה וסנכרון קבצי מדיה" : "Media Library Scanner"}
              </h3>
              <p className="text-xs text-neutral-400">
                {isHebrew
                  ? "סורק את תיקיית הסרטים בשרת ומחבר מטא-דאטה, פוסטרים ותרגומים אוטומטית מול TMDB."
                  : "Scans server folders and automatically correlates metadata, posters, and subtitles with TMDB."}
              </p>
            </div>
            <button
              onClick={handleTriggerScan}
              className="px-5 py-2.5 rounded-xl bg-[#E50914] text-white font-bold text-xs hover:bg-[#b80710] shadow-lg shadow-[#E50914]/30 cursor-pointer flex items-center gap-2"
            >
              <FolderSync className="w-4 h-4" />
              <span>{isHebrew ? "הפעל סריקת סרטים מלאה" : "Trigger Full Scan"}</span>
            </button>
          </div>

          <h3 className="text-base font-bold text-white pt-4">
            {isHebrew ? "קבצים שלא זוהו אוטומטית (Unmatched)" : "Unmatched Media Items"}
          </h3>

          {!summary?.unmatchedItems || summary.unmatchedItems.length === 0 ? (
            <div className="py-12 text-center text-neutral-500 space-y-2">
              <CheckCircle className="w-10 h-10 mx-auto text-emerald-500" />
              <p className="text-base font-semibold text-white">
                {isHebrew ? "כל קובצי המדיה זוהו ומחוברים בהצלחה!" : "All media items matched"}
              </p>
              <p className="text-xs">
                {isHebrew ? "אין קבצים לא מזוהים במסד הנתונים של הספרייה." : "No unmatched files in the library database."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {summary.unmatchedItems.map((item: any) => (
                <div
                  key={item.id}
                  className="p-4 rounded-xl bg-neutral-900/60 border border-white/10 flex items-center justify-between"
                >
                  <div>
                    <h4 className="text-sm font-bold text-white">{item.title}</h4>
                    <span className="text-xs text-neutral-400">{item.year || "Unknown Year"}</span>
                  </div>
                  <button
                    onClick={() => {
                      setFixMatchItem(item);
                      setAliasInput(item.title);
                    }}
                    className="px-3 py-1.5 rounded-lg bg-[#E50914] text-xs font-bold hover:bg-[#b80710] transition-colors cursor-pointer"
                  >
                    {isHebrew ? "חבר סרט ידנית" : "Fix Match"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Fix Match Modal */}
          {fixMatchItem && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
              <div className="w-full max-w-md bg-neutral-900 border border-white/20 rounded-2xl p-6 shadow-2xl animate-modal-sheet">
                <h3 className="text-lg font-bold text-white mb-2">Fix Match for "{fixMatchItem.title}"</h3>
                <p className="text-xs text-neutral-400 mb-4">
                  Find the movie on <a href="https://www.themoviedb.org" target="_blank" rel="noreferrer" className="text-[#E50914] underline">themoviedb.org</a> and enter its TMDB numeric ID below.
                </p>

                <form onSubmit={handleFixMatch} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      TMDB ID
                    </label>
                    <input
                      type="number"
                      required
                      value={tmdbIdInput}
                      onChange={(e) => setTmdbIdInput(e.target.value)}
                      placeholder="e.g. 550 for Fight Club"
                      className="w-full px-3 py-2 bg-neutral-950 border border-white/20 rounded-lg text-sm text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-neutral-400 uppercase mb-1">
                      Persistent Search Alias
                    </label>
                    <input
                      type="text"
                      value={aliasInput}
                      onChange={(e) => setAliasInput(e.target.value)}
                      placeholder="e.g. filename alias"
                      className="w-full px-3 py-2 bg-neutral-950 border border-white/20 rounded-lg text-sm text-white"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setFixMatchItem(null)}
                      className="px-4 py-2 rounded-lg bg-white/10 text-sm font-semibold hover:bg-white/20"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-lg bg-[#E50914] text-sm font-bold hover:bg-[#b80710]"
                    >
                      Save & Link
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB 5: Settings & Maintenance ─── */}
      {activeTab === "settings" && (
        <div className="py-8 space-y-8 max-w-2xl animate-in fade-in">
          <form onSubmit={handleSaveSettings} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                {isHebrew ? "שם השרת (Server Display Name)" : "Server Display Name"}
              </label>
              <input
                type="text"
                value={settings["SERVER_NAME"] || "Homeflix Cinema"}
                onChange={(e) => setSettings({ ...settings, SERVER_NAME: e.target.value })}
                className="w-full px-4 py-2.5 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E50914]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                The Movie Database (TMDB) API Key
              </label>
              <input
                type="text"
                value={settings["TMDB_API_KEY"] || ""}
                onChange={(e) => setSettings({ ...settings, TMDB_API_KEY: e.target.value })}
                placeholder="Enter your TMDB API v3 key"
                className="w-full px-4 py-2.5 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E50914]"
              />
              <p className="text-xs text-neutral-500 mt-1">
                {isHebrew ? "מאפשר קבלת תקצירים בעברית, פוסטרים רשמיים, שחקנים וטריילרים." : "Enables official metadata, posters, cast, crew, and Hebrew summaries."}
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                Real-Debrid API Key (הורדות מהירות ועוקפות ספקים)
              </label>
              <input
                type="text"
                value={settings["REAL_DEBRID_API_KEY"] || ""}
                onChange={(e) => setSettings({ ...settings, REAL_DEBRID_API_KEY: e.target.value })}
                placeholder="Enter your Real-Debrid API token"
                className="w-full px-4 py-2.5 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E50914]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                {isHebrew ? "האצת חומרה לטרנסקודינג (כרטיס מסך)" : "Transcoding Hardware Acceleration"}
              </label>
              <select
                value={settings["TRANSCODE_HW_ACCEL"] || "auto"}
                onChange={(e) => setSettings({ ...settings, TRANSCODE_HW_ACCEL: e.target.value })}
                className="w-full px-4 py-2.5 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E50914]"
              >
                <option value="auto">Auto (Detect NVIDIA NVENC)</option>
                <option value="nvidia">Force NVIDIA NVENC (RTX GPU)</option>
                <option value="cpu">Force Software / CPU (libx264)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                {isHebrew ? "שפת ממשק ברירת מחדל" : "Default Server Language"}
              </label>
              <select
                value={settings["DEFAULT_LANGUAGE"] || "he"}
                onChange={(e) => setSettings({ ...settings, DEFAULT_LANGUAGE: e.target.value })}
                className="w-full px-4 py-2.5 bg-neutral-900 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E50914]"
              >
                <option value="he">עברית (Hebrew RTL)</option>
                <option value="en">English (LTR)</option>
              </select>
            </div>

            <button
              type="submit"
              className="px-6 py-2.5 rounded-lg bg-[#E50914] text-white font-bold text-sm hover:bg-[#b80710] transition-colors shadow-lg shadow-[#E50914]/30 cursor-pointer"
            >
              {isHebrew ? "שמור הגדרות מערכת" : "Save Settings"}
            </button>
          </form>

          {/* Maintenance Actions */}
          <div className="pt-8 border-t border-white/10 space-y-4">
            <h3 className="text-base font-bold text-white">{isHebrew ? "תחזוקת מערכת וניקוי" : "System Maintenance"}</h3>
            <div className="p-4 rounded-xl bg-neutral-900/60 border border-white/10 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-white">
                  {isHebrew ? "ניקוי מטמון טרנסקודינג ו-Redis" : "Clear Transcoding & Redis Cache"}
                </h4>
                <p className="text-xs text-neutral-400">
                  {isHebrew
                    ? "מפנה שטח דיסק בקבצי הטרנסקודינג הזמניים ומרענן מטא-דאטה במטמון."
                    : "Frees up disk space in `/media/windows/omflix/transcodes` and refreshes metadata."}
                </p>
              </div>
              <button
                onClick={handleClearCache}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-xs font-bold text-white transition-colors cursor-pointer"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
                <span>{isHebrew ? "נקה מטמון" : "Clear Cache"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 6: Webhook Notifications (Discord & Telegram) ─── */}
      {activeTab === "webhooks" && (
        <div className="py-8 space-y-8 max-w-3xl animate-in fade-in">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">
              {isHebrew ? "התראות אוטומטיות (Discord & Telegram)" : "Automated Webhook Notifications"}
            </h2>
            <p className="text-xs text-neutral-400">
              {isHebrew
                ? "קבל התראות מיידיות ל-Discord או Telegram בכל פעם שסרט חדש נוסף לספרייה, סריקה מסתיימת, או מתרחשת תקלת מערכת."
                : "Receive real-time alerts to Discord or Telegram when new media is indexed, scans finish, or errors occur."}
            </p>
          </div>

          <form onSubmit={handleSaveWebhooks} className="space-y-6">
            {/* Discord Webhook */}
            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#5865F2]/20 border border-[#5865F2]/30 flex items-center justify-center text-[#5865F2] font-black text-sm">
                    D
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Discord Webhook</h3>
                    <p className="text-xs text-neutral-400">
                      {isHebrew ? "שלח הודעות מעוצבות לערוץ Discord נבחר" : "Post formatted embed cards to a Discord channel"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleTestWebhook("discord")}
                  disabled={!webhookSettings.discordWebhookUrl || testingWebhook === "discord"}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5865F2] hover:bg-[#4752c4] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-bold text-white transition-all cursor-pointer shadow"
                >
                  <Send className={`w-3.5 h-3.5 ${testingWebhook === "discord" ? "animate-spin" : ""}`} />
                  <span>{isHebrew ? "שלח בדיקה" : "Send Test"}</span>
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                  Webhook URL
                </label>
                <input
                  type="url"
                  placeholder="https://discord.com/api/webhooks/..."
                  value={webhookSettings.discordWebhookUrl || ""}
                  onChange={(e) => setWebhookSettings({ ...webhookSettings, discordWebhookUrl: e.target.value })}
                  className="w-full px-4 py-2.5 bg-neutral-950 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#5865F2]"
                />
              </div>
            </div>

            {/* Telegram Bot */}
            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#229ED9]/20 border border-[#229ED9]/30 flex items-center justify-center text-[#229ED9] font-black text-sm">
                    T
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Telegram Bot</h3>
                    <p className="text-xs text-neutral-400">
                      {isHebrew ? "קבל הודעות ישירות מהבוט לצ'אט או ערוץ טלגרם" : "Direct bot notifications to a Telegram chat or group"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleTestWebhook("telegram")}
                  disabled={!webhookSettings.telegramBotToken || !webhookSettings.telegramChatId || testingWebhook === "telegram"}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#229ED9] hover:bg-[#1b85b8] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-bold text-white transition-all cursor-pointer shadow"
                >
                  <Send className={`w-3.5 h-3.5 ${testingWebhook === "telegram" ? "animate-spin" : ""}`} />
                  <span>{isHebrew ? "שלח בדיקה" : "Send Test"}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                    Bot Token (@BotFather)
                  </label>
                  <input
                    type="password"
                    placeholder="123456789:ABCdefGHI..."
                    value={webhookSettings.telegramBotToken || ""}
                    onChange={(e) => setWebhookSettings({ ...webhookSettings, telegramBotToken: e.target.value })}
                    className="w-full px-4 py-2.5 bg-neutral-950 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#229ED9]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-neutral-300 uppercase mb-1">
                    Chat ID / Group ID
                  </label>
                  <input
                    type="text"
                    placeholder="-100123456789"
                    value={webhookSettings.telegramChatId || ""}
                    onChange={(e) => setWebhookSettings({ ...webhookSettings, telegramChatId: e.target.value })}
                    className="w-full px-4 py-2.5 bg-neutral-950 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#229ED9]"
                  />
                </div>
              </div>
            </div>

            {/* Notification Triggers */}
            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 space-y-3">
              <h4 className="text-xs font-bold text-neutral-300 uppercase tracking-wider">
                {isHebrew ? "אירועים לשליחת התראה" : "Notification Triggers"}
              </h4>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={webhookSettings.notifyOnScan !== false}
                    onChange={(e) => setWebhookSettings({ ...webhookSettings, notifyOnScan: e.target.checked })}
                    className="w-4 h-4 rounded text-[#E50914] bg-neutral-800 border-white/20 focus:ring-0 cursor-pointer"
                  />
                  <span className="text-sm text-neutral-200">
                    {isHebrew ? "סיום סריקת ספרייה וזיהוי קבצים חדשים" : "Library scan completed & new media indexed"}
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={webhookSettings.notifyOnDownload !== false}
                    onChange={(e) => setWebhookSettings({ ...webhookSettings, notifyOnDownload: e.target.checked })}
                    className="w-4 h-4 rounded text-[#E50914] bg-neutral-800 border-white/20 focus:ring-0 cursor-pointer"
                  />
                  <span className="text-sm text-neutral-200">
                    {isHebrew ? "סיום הורדת תוכן Real-Debrid / טורנט" : "Real-Debrid / Torrent download finished"}
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={webhookSettings.notifyOnError !== false}
                    onChange={(e) => setWebhookSettings({ ...webhookSettings, notifyOnError: e.target.checked })}
                    className="w-4 h-4 rounded text-[#E50914] bg-neutral-800 border-white/20 focus:ring-0 cursor-pointer"
                  />
                  <span className="text-sm text-neutral-200">
                    {isHebrew ? "שגיאות טרנסקודינג וקריסות שרת" : "Transcoding errors & critical server alerts"}
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="px-6 py-2.5 rounded-lg bg-[#E50914] text-white font-bold text-sm hover:bg-[#b80710] transition-colors shadow-lg shadow-[#E50914]/30 cursor-pointer"
            >
              {isHebrew ? "שמור הגדרות התראות" : "Save Webhook Settings"}
            </button>
          </form>
        </div>
      )}

      {/* ─── TAB 7: Active User Sessions ─── */}
      {activeTab === "sessions" && (
        <div className="py-8 space-y-6 animate-in fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">
                {isHebrew ? "סשנים מחוברים בזמן אמת" : "Connected Active Sessions"}
              </h2>
              <p className="text-xs text-neutral-400">
                {isHebrew
                  ? "כל המכשירים והדפדפנים המחוברים כרגע למערכת. באפשרותך לנתק סשן בודד בלחיצה."
                  : "All devices and browsers currently authenticated. You can revoke any active session instantly."}
              </p>
            </div>
          </div>

          {sessionsList.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-neutral-900/60 border border-white/10 text-neutral-400">
              <Laptop className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-semibold">
                {isHebrew ? "לא נמצאו סשנים פעילים" : "No active sessions found"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-neutral-900/60">
              <table className="w-full text-left text-sm text-neutral-300" dir="ltr">
                <thead className="bg-white/5 text-xs uppercase font-bold text-neutral-400 border-b border-white/10">
                  <tr>
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Device / Browser</th>
                    <th className="px-5 py-3">IP Address</th>
                    <th className="px-5 py-3">Connected At</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {sessionsList.map((session) => (
                    <tr key={session.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="font-bold text-white">{session.displayName || session.username}</div>
                        <div className="text-xs text-neutral-500 font-mono">@{session.username}</div>
                      </td>
                      <td className="px-5 py-3.5 max-w-xs truncate text-xs text-neutral-300" title={session.userAgent || "Unknown"}>
                        {session.userAgent || "Unknown Client"}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-mono text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                          {session.ipAddress || "Localhost"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-neutral-400">
                        {new Date(session.createdAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={() => handleRevokeSession(session.id)}
                          className="px-3 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 text-xs font-semibold border border-red-500/20 transition-all cursor-pointer"
                        >
                          {isHebrew ? "נתק סשן" : "Revoke"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB 8: Playback & Diagnostic Logs ─── */}
      {activeTab === "logs" && (
        <div className="py-8 space-y-6 animate-in fade-in">
          {/* Logs Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-neutral-400">
                  {isHebrew ? "סה״כ אירועים מתועדים" : "Total Logged Events"}
                </p>
                <h3 className="text-2xl font-black text-white mt-1">
                  {logsSummary?.totalLogs ?? playbackLogs.length}
                </h3>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400">
                <FileText className="w-5 h-5" />
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-neutral-400">
                  {isHebrew ? "שגיאות שזוהו" : "Errors Detected"}
                </p>
                <h3 className="text-2xl font-black text-rose-400 mt-1">
                  {logsSummary?.errorsCount ?? 0}
                </h3>
              </div>
              <div className="p-3 rounded-xl bg-rose-500/10 text-rose-400">
                <AlertTriangle className="w-5 h-5" />
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-neutral-400">
                  {isHebrew ? "אזהרות" : "Warnings"}
                </p>
                <h3 className="text-2xl font-black text-amber-400 mt-1">
                  {logsSummary?.warningsCount ?? 0}
                </h3>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-neutral-900/60 border border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-neutral-400">
                  {isHebrew ? "מעברי שידור / סיבובים" : "Stream / Carousel Transitions"}
                </p>
                <h3 className="text-2xl font-black text-emerald-400 mt-1">
                  {logsSummary?.switchesCount ?? 0}
                </h3>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400">
                <RefreshCw className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Action & Filter Bar */}
          <div className="p-4 rounded-2xl bg-neutral-900/60 border border-white/10 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  placeholder={isHebrew ? "חיפוש בלוגים (שם סרט, אירוע)..." : "Search logs (title, event)..."}
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  className="pl-9 pr-4 py-1.5 rounded-xl bg-black/40 border border-white/10 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-white/30 w-64"
                />
              </div>

              {/* Category Filter */}
              <select
                value={logsCategoryFilter}
                onChange={(e) => setLogsCategoryFilter(e.target.value)}
                className="px-3 py-1.5 rounded-xl bg-black/40 border border-white/10 text-xs text-neutral-300 focus:outline-none cursor-pointer"
              >
                <option value="ALL">{isHebrew ? "כל הקטגוריות" : "All Categories"}</option>
                <option value="CAROUSEL">CAROUSEL</option>
                <option value="ONLINE_STREAM">ONLINE_STREAM</option>
                <option value="VIDEO_PLAYER">VIDEO_PLAYER</option>
                <option value="MEDIA_NAV">MEDIA_NAV</option>
                <option value="SYSTEM">SYSTEM</option>
              </select>

              {/* Level Filter */}
              <select
                value={logsLevelFilter}
                onChange={(e) => setLogsLevelFilter(e.target.value)}
                className="px-3 py-1.5 rounded-xl bg-black/40 border border-white/10 text-xs text-neutral-300 focus:outline-none cursor-pointer"
              >
                <option value="ALL">{isHebrew ? "כל הרמות" : "All Levels"}</option>
                <option value="ERROR">ERROR</option>
                <option value="WARN">WARN</option>
                <option value="INFO">INFO</option>
                <option value="DEBUG">DEBUG</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setLogsLoading(true);
                  api.getPlaybackLogs({
                    category: logsCategoryFilter === "ALL" ? undefined : logsCategoryFilter,
                    level: logsLevelFilter === "ALL" ? undefined : logsLevelFilter,
                    search: logsSearch || undefined,
                    limit: 150,
                  }).then((l) => setPlaybackLogs(l)).finally(() => setLogsLoading(false));
                }}
                disabled={logsLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold border border-white/10 transition-all cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? "animate-spin" : ""}`} />
                <span>{isHebrew ? "רענן" : "Refresh"}</span>
              </button>

              <button
                onClick={async () => {
                  if (!confirm(isHebrew ? "האם למחוק את כל הלוגים?" : "Clear all logs?")) return;
                  await api.clearPlaybackLogs();
                  setPlaybackLogs([]);
                  setLogsSummary(null);
                  setActionMessage(isHebrew ? "הלוגים נמחקו" : "Logs cleared");
                  setTimeout(() => setActionMessage(null), 3000);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-semibold border border-red-500/20 transition-all cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isHebrew ? "נקה יומן" : "Clear Logs"}</span>
              </button>
            </div>
          </div>

          {/* Logs Table / Stream */}
          {playbackLogs.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-neutral-900/40 border border-white/10 text-neutral-500">
              <Terminal className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-semibold">
                {isHebrew ? "לא נמצאו רשומות יומן תואמות" : "No playback log entries found"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-neutral-900/60 font-mono text-xs">
              <table className="w-full text-left text-neutral-300" dir="ltr">
                <thead className="bg-white/5 uppercase font-bold text-neutral-400 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Time</th>
                    <th className="px-3 py-3">Level</th>
                    <th className="px-3 py-3">Category</th>
                    <th className="px-4 py-3">Event</th>
                    <th className="px-4 py-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {playbackLogs.map((entry) => {
                    const isExpanded = expandedLogId === entry.id;
                    const levelColors: Record<string, string> = {
                      ERROR: "bg-rose-500/15 text-rose-400 border-rose-500/30",
                      WARN: "bg-amber-500/15 text-amber-300 border-amber-500/30",
                      INFO: "bg-blue-500/15 text-blue-300 border-blue-500/30",
                      DEBUG: "bg-neutral-500/15 text-neutral-400 border-neutral-500/30",
                    };

                    return (
                      <React.Fragment key={entry.id}>
                        <tr
                          onClick={() => setExpandedLogId(isExpanded ? null : entry.id)}
                          className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-2.5 text-neutral-400 whitespace-nowrap text-[11px]">
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${levelColors[entry.level] || levelColors.INFO}`}>
                              {entry.level}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-[11px] text-neutral-300 font-semibold">
                              {entry.category}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-medium text-white max-w-xs truncate" title={entry.event}>
                            {entry.event}
                          </td>
                          <td className="px-4 py-2.5 text-neutral-400 max-w-sm truncate text-[11px]">
                            {entry.details ? JSON.stringify(entry.details) : "—"}
                          </td>
                        </tr>
                        {isExpanded && entry.details && (
                          <tr className="bg-black/50">
                            <td colSpan={5} className="px-6 py-4">
                              <pre className="p-3 rounded-xl bg-black/80 border border-white/10 text-[11px] text-emerald-400 overflow-x-auto whitespace-pre-wrap">
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
